/** Stateless demo flow — no DB events. Session in localStorage + optional demo cookie. */

import { clearIdentity } from "./api";
import { demoProfileThumbnailUrl } from "./demoProfileThumbnails";
import { DEMO_HOST_SCHOOL_PROMO } from "./demoSchoolExperience";
import { normalizePhone } from "./phone";
import {
  canDemoVote,
  getDemoAggregate,
  getDemoEventTotalPicks,
  getDemoMyAppreciationsReceived,
} from "../games/favoriteDances/demo/demoState.js";

export {
  canDemoVote,
  getDemoAggregate,
  getDemoEventTotalPicks,
  getDemoMyAppreciationsReceived,
};

export const DEMO_EVENT_ID = "demo-today";
export const DEMO_COOKIE = "rsvp_demo";
export const DEMO_STATE_KEY = "ln_demo_state_v1";

/** Local-only demo guest IDs — never hit the real API. */
export function isDemoDancerId(dancerId) {
  return typeof dancerId === "string" && dancerId.startsWith("demo-user-");
}

const TEMPLATES = [
  {
    name: "Salsa Social",
    description: "Cuban + LA salsa all night. Beginners welcome — first hour is a free intro class!",
    location: "Sala Caracas, Berlin",
    cover_color: "pink",
    pricing: { type: "paid-at-venue", amount: 12, currency: "EUR" },
    organizer_name: "Marta",
  },
  {
    name: "Bachata Sensual Night",
    description: "Slow, sensual, sweet. Cool-down vibes after a long week.",
    location: "Mambo Loft, Kreuzberg",
    cover_color: "purple",
    pricing: { type: "free" },
    organizer_name: "Diego",
  },
  {
    name: "Kizomba & Urban Kiz",
    description: "Smooth connection on the floor. All levels welcome.",
    location: "Tanzfabrik, Berlin",
    cover_color: "orchid",
    pricing: { type: "paid-at-venue", amount: 10, currency: "EUR" },
    organizer_name: "Marta",
  },
  {
    name: "Latin Mix Social",
    description: "Salsa, bachata, and merengue in one room. DJ set until late.",
    location: "Club Corazon, Berlin",
    cover_color: "accent",
    pricing: { type: "paid-at-venue", amount: 15, currency: "EUR" },
    organizer_name: "Diego",
  },
  {
    name: "Afro-Latin Fusion",
    description: "Timba, salsa, and afro beats.",
    location: "Yaam Beach, Berlin",
    cover_color: "night",
    pricing: { type: "free" },
    organizer_name: "Marta",
  },
];

const FIXTURE_BOARD_MESSAGES = [
  {
    id: "demo-msg-1",
    author: { name: "Carla", dancer_id: "demo-guest-carla" },
    content: "Anyone sharing a cab to Warschauer after?",
    created_at: new Date(Date.now() - 45 * 60 * 1000).toISOString(),
    is_mine: false,
    is_pinned: false,
    is_host_message: false,
  },
  {
    id: "demo-msg-2",
    author: { name: "Marco", dancer_id: "demo-guest-marco" },
    content: "Water at the bar is €3 — bring cash",
    created_at: new Date(Date.now() - 28 * 60 * 1000).toISOString(),
    is_mine: false,
    is_pinned: true,
    is_host_message: false,
  },
  {
    id: "demo-msg-3",
    author: { name: "Marta", dancer_id: "demo-host-marta" },
    content: "Beginners' intro class starts in the small room in 10 min!",
    created_at: new Date(Date.now() - 15 * 60 * 1000).toISOString(),
    is_mine: false,
    is_pinned: false,
    is_host_message: true,
  },
];

const BOARD_GUEST_MAX = 5;
const BOARD_COOLDOWN_MS = 30 * 1000;

const FIXTURE_GUESTS = [
  { dancer_id: "demo-guest-carla", name: "Carla", role: "Follower", dance_forms: [{ name: "Salsa", level: "Intermediate" }], checked_in_at: true, phone: "+49 170 1234567", thumbnail_url: demoProfileThumbnailUrl("carla") },
  { dancer_id: "demo-guest-marco", name: "Marco", role: "Leader", dance_forms: [{ name: "Salsa", level: "Advanced" }], checked_in_at: true, phone: "+49 170 7654321", thumbnail_url: demoProfileThumbnailUrl("marco") },
  { dancer_id: "demo-guest-lina", name: "Lina", role: "Follower", dance_forms: [{ name: "Bachata", level: "Beginner" }], first_timer: true, checked_in_at: true, phone: "+49 170 2223344", thumbnail_url: demoProfileThumbnailUrl("lina") },
  { dancer_id: "demo-guest-tomas", name: "Tomas", role: "Leader", dance_forms: [{ name: "Salsa", level: "Improver" }], checked_in_at: true, phone: "+49 170 3334455", thumbnail_url: demoProfileThumbnailUrl("tomas") },
  { dancer_id: "demo-guest-yuki", name: "Yuki", role: "Both", dance_forms: [{ name: "Kizomba", level: "Intermediate" }], checked_in_at: true, phone: "+49 170 5556677", thumbnail_url: demoProfileThumbnailUrl("yuki") },
  { dancer_id: "demo-guest-sofia", name: "Sofia", role: "Follower", dance_forms: [{ name: "Salsa", level: "Advanced" }], phone: "+49 170 6667788", thumbnail_url: demoProfileThumbnailUrl("sofia") },
  { dancer_id: "demo-guest-jamal", name: "Jamal", role: "Leader", dance_forms: [{ name: "Bachata", level: "Intermediate" }], phone: "+49 170 7778899", thumbnail_url: demoProfileThumbnailUrl("jamal") },
  { dancer_id: "demo-guest-elena", name: "Elena", role: "Follower", dance_forms: [{ name: "Salsa", level: "Improver" }], phone: "+49 170 8889900", thumbnail_url: demoProfileThumbnailUrl("elena") },
];

function utcDateKey(d = new Date()) {
  return d.toISOString().slice(0, 10);
}

function dateSeed(dateKey) {
  let h = 0;
  for (let i = 0; i < dateKey.length; i += 1) {
    h = (Math.imul(31, h) + dateKey.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

function randomId(n = 12) {
  const bytes = crypto.getRandomValues(new Uint8Array(n));
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("").slice(0, n);
}

export function activateDemoSession(fromUrl = false) {
  if (typeof document === "undefined") return;
  if (fromUrl) {
    document.cookie = `${DEMO_COOKIE}=1; path=/; max-age=86400; SameSite=Lax`;
  }
}

export function isDemoSessionActive() {
  if (typeof document === "undefined") return false;
  return document.cookie.split(";").some((c) => c.trim().startsWith(`${DEMO_COOKIE}=`));
}

/** Clear demo progress and reload. Defaults to the demo event page. */
export function resetDemo({ redirectTo = "/demo?demo=true" } = {}) {
  try {
    localStorage.removeItem(DEMO_STATE_KEY);
  } catch {
    /* private browsing / quota */
  }
  clearIdentity();
  activateDemoSession(true);
  if (typeof window !== "undefined") {
    window.location.assign(redirectTo);
  }
}

export function readDemoState() {
  try {
    const raw = localStorage.getItem(DEMO_STATE_KEY);
    if (!raw) return null;
    const state = JSON.parse(raw);
    if (state.date_key !== utcDateKey()) return null;
    return state;
  } catch {
    return null;
  }
}

function writeDemoState(state) {
  localStorage.setItem(DEMO_STATE_KEY, JSON.stringify({ ...state, date_key: utcDateKey() }));
}

/** Used by game demo modules to persist localStorage state. */
export function writeDemoStateForGame(state) {
  writeDemoState(state);
}

/** Today's synthetic event — deterministic pick from templates, starts ~2h ago so check-in works. */
export function getTodaysDemoEvent(now = new Date()) {
  const dateKey = utcDateKey(now);
  const template = TEMPLATES[dateSeed(dateKey) % TEMPLATES.length];
  const startsAt = new Date(now.getTime() - 2 * 60 * 60 * 1000);

  return {
    id: DEMO_EVENT_ID,
    name: template.name,
    description: template.description,
    location: template.location,
    city: "Berlin",
    city_slug: "berlin",
    public_slug: "demo-night",
    public_path: "/demo",
    starts_at: startsAt.toISOString(),
    occurrence_date: dateKey,
    cover_color: template.cover_color,
    pricing: template.pricing,
    organizer_name: template.organizer_name,
    is_live: true,
    is_demo: true,
    lifecycle: "published",
    host_school: DEMO_HOST_SCHOOL_PROMO,
    host_school_opt_out: false,
    counts: { total: FIXTURE_GUESTS.length, balanced_leaders: 4, balanced_followers: 4, both: 0 },
    landing: {
      eyebrow: "No account needed",
      subtitle: "RSVP as a guest or switch to host view. Setup takes under 2 minutes.",
      cta: "Join the guest list →",
    },
  };
}

/** First N fixture guests for landing-page social proof (faces + names). */
export function getDemoLandingPreview(limit = 3) {
  return FIXTURE_GUESTS.slice(0, Math.max(0, limit)).map((g) => ({
    dancer_id: g.dancer_id,
    name: g.name,
    role: g.role,
    thumbnail_url: g.thumbnail_url,
    hide_name: false,
  }));
}

export function getDemoGuestList(state) {
  const rsvps = FIXTURE_GUESTS.map((g) => ({
    id: g.dancer_id,
    dancer_id: g.dancer_id,
    name: g.name,
    role: g.role,
    dance_forms: g.dance_forms,
    first_timer: g.first_timer || false,
    attendance_status: "likely",
    phone: g.phone || "",
    checked_in_at: g.checked_in_at ? new Date().toISOString() : null,
    hide_name: false,
    thumbnail_url: g.thumbnail_url,
  }));

  if (state?.my_rsvp) {
    const hide = state.my_rsvp.hide_name || false;
    const mine = {
      id: state.my_rsvp.dancer_id,
      dancer_id: state.my_rsvp.dancer_id,
      name: hide ? "Someone" : state.my_rsvp.name,
      role: state.my_rsvp.role,
      dance_forms: state.my_rsvp.dance_forms || [],
      first_timer: state.my_rsvp.first_timer || false,
      attendance_status: state.my_rsvp.attendance_status || "likely",
      phone: state.my_rsvp.phone || "",
      checked_in_at: state.checked_in_at || null,
      hide_name: hide,
    };
    const idx = rsvps.findIndex((r) => r.dancer_id === mine.dancer_id);
    if (idx >= 0) rsvps[idx] = { ...rsvps[idx], ...mine };
    else rsvps.unshift(mine);
  }

  return rsvps;
}

export function getDemoCheckedIn(state) {
  const checked = FIXTURE_GUESTS.filter((g) => g.checked_in_at).map((g) => ({
    dancer_id: g.dancer_id,
    name: g.name,
    role: g.role,
    dance_forms: g.dance_forms,
    thumbnail_url: g.thumbnail_url,
  }));

  if (state?.checked_in_at && state?.my_rsvp) {
    const mine = {
      dancer_id: state.my_rsvp.dancer_id,
      name: state.my_rsvp.name,
      role: state.my_rsvp.role,
      dance_forms: state.my_rsvp.dance_forms || [],
    };
    if (!checked.some((d) => d.dancer_id === mine.dancer_id)) {
      checked.unshift(mine);
    }
  }

  return checked;
}

export function saveDemoRsvp(
  { name, role, phone, attendance_status = "likely", first_timer = false, hide_name = false },
  existingState,
) {
  const dancerId = existingState?.my_rsvp?.dancer_id || `demo-user-${randomId(8)}`;
  const myRsvp = {
    dancer_id: dancerId,
    name: name.trim(),
    role,
    phone: normalizePhone(phone) || "",
    attendance_status,
    first_timer,
    hide_name: !!hide_name,
    dance_forms: [],
  };
  const state = {
    ...(existingState || {}),
    my_rsvp: myRsvp,
    checked_in_at: null,
    checked_out_at: null,
    floor_attended_at: null,
    votes_submitted: false,
  };
  writeDemoState(state);
  return { state, dancerId, myRsvp };
}

export function demoCheckIn(state) {
  if (!state?.my_rsvp) return state;
  const now = new Date().toISOString();
  const next = {
    ...state,
    checked_in_at: now,
    floor_attended_at: state.floor_attended_at || now,
  };
  writeDemoState(next);
  return next;
}

export function demoCheckOut(state) {
  if (!state?.my_rsvp) return state;
  const next = {
    ...state,
    checked_in_at: null,
    checked_out_at: new Date().toISOString(),
  };
  writeDemoState(next);
  return next;
}

function mapUserBoardMessage(msg, myDancerId) {
  return {
    ...msg,
    is_mine: msg.author?.dancer_id === myDancerId,
    is_pinned: false,
    is_host_message: false,
  };
}

export function getDemoBoard(state) {
  const myId = state?.my_rsvp?.dancer_id;
  const userMessages = (state?.board_messages || []).map((m) => mapUserBoardMessage(m, myId));
  const fixture = FIXTURE_BOARD_MESSAGES.map((m) => ({ ...m, is_mine: false }));
  const messages = [...fixture, ...userMessages].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  );
  const myPostCount = userMessages.length;
  const lastMine = userMessages
    .filter((m) => m.is_mine)
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
  const cooldownMs = lastMine
    ? Math.max(0, BOARD_COOLDOWN_MS - (Date.now() - new Date(lastMine.created_at).getTime()))
    : 0;

  return {
    messages,
    my_post_count: myPostCount,
    is_hosting: false,
    cooldown_ms: cooldownMs,
    at_post_limit: myPostCount >= BOARD_GUEST_MAX,
  };
}

export function postDemoBoardMessage(state, content) {
  if (!state?.my_rsvp || !state.checked_in_at) return state;
  const board = getDemoBoard(state);
  if (board.at_post_limit || board.cooldown_ms > 0) return state;
  const trimmed = content.trim().slice(0, 280);
  if (!trimmed) return state;

  const message = {
    id: `demo-user-msg-${randomId(8)}`,
    author: { name: state.my_rsvp.name, dancer_id: state.my_rsvp.dancer_id },
    content: trimmed,
    created_at: new Date().toISOString(),
  };
  const next = { ...state, board_messages: [...(state.board_messages || []), message] };
  writeDemoState(next);
  return next;
}

export function deleteDemoBoardMessage(state, messageId) {
  const myId = state?.my_rsvp?.dancer_id;
  const nextMessages = (state?.board_messages || []).filter(
    (m) => !(m.id === messageId && m.author?.dancer_id === myId),
  );
  if (nextMessages.length === (state?.board_messages || []).length) return state;
  const next = { ...state, board_messages: nextMessages };
  writeDemoState(next);
  return next;
}

export function demoLandingCopy(event) {
  const landing = event.landing || {};
  return {
    eyebrow: landing.eyebrow || "No account needed",
    title: event.name,
    subtitle:
      landing.subtitle ||
      "RSVP as a guest or switch to host view. Setup takes under 2 minutes.",
    stepsLabel: landing.stepsLabel || null,
    cta: landing.cta || "Join the guest list →",
  };
}

/** Funnel step index (1–3) and completed flags for DemoProgress. */
export function getDemoFunnelProgress(state, pathname = "") {
  const myRsvp = Boolean(state?.my_rsvp);
  const checkedIn = Boolean(state?.checked_in_at);
  const canVote = canDemoVote(state);
  const voted = Boolean(state?.votes_submitted);
  const onVote = pathname.includes("/vote");
  const onCheckIn = pathname.includes("/check-in");

  let current = 1;
  if (onVote || canVote || voted) current = 3;
  else if (onCheckIn || checkedIn || myRsvp) current = 2;

  return {
    current,
    completed: {
      rsvp: myRsvp,
      floor: Boolean(state?.floor_attended_at),
      vote: voted,
    },
  };
}

/** One-line next-step hint for demo event / check-in screens. */
export function demoNextStepHint(state) {
  if (!state?.my_rsvp) return null;
  if (state.checked_in_at) {
    return "You're on the floor. Check out when you're ready — that unlocks Favorite Dances voting.";
  }
  if (canDemoVote(state)) return "You're checked out. Tap below to vote for your favorite dancers tonight.";
  if (state.votes_submitted) return "Votes sent! Reset to run through the demo again.";
  return "Next: tap Check in — the demo jumps to tonight's floor.";
}
